import React from 'react';

type Direction = 1 | -1 | 0;
type SelectedKey = string | null;

interface SplitButtonProps {
    aiDirection: Direction;
    confidence: number;
    selected: SelectedKey;
    onSelect: (key: string) => void;
    neutral?: boolean;
}

const MIN_VISUAL_PCT = 25;
const MAX_VISUAL_PCT = 75;

export const SignalDirectionSplitButton: React.FC<SplitButtonProps> = ({
                                                                           aiDirection,
                                                                           confidence,
                                                                           selected,
                                                                           onSelect,
                                                                           neutral = false,
                                                                       }) => {
    const confidenceUnavailable = null == confidence || !Number.isFinite(confidence)
        || confidence < 0 || confidence > 100;
    const safeConf = confidenceUnavailable
        ? 50 : confidence;

    const forwardPct = neutral ? 50 : safeConf;
    const reversePct = 100 - forwardPct;
    const visualForwardPct = neutral ? 50
        : Math.max(MIN_VISUAL_PCT, Math.min(MAX_VISUAL_PCT, forwardPct));

    const isBull = 1 === aiDirection;

    if (neutral) {
        return (
            <div className="sd-split-container">
                <div className="sd-split-neutral">
                    <button
                        className={`sd-btn sd-btn-neutral sd-long ${'MANUAL_LONG' === selected ? ' selected' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect('FORWARD');
                        }}
                        style={{width: '50%'}}
                        aria-label="手动开多"
                    >
                        <span className="sd-btn-main">开多</span>
                        <span className="sd-btn-sub">手动</span>
                    </button>
                    <button
                        className={`sd-btn sd-btn-neutral sd-short ${'MANUAL_SHORT' === selected ? ' selected' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect('REVERSE');
                        }}
                        style={{width: '50%'}}
                        aria-label="手动开空"
                    >
                        <span className="sd-btn-main">开空</span>
                        <span className="sd-btn-sub">手动</span>
                    </button>
                </div>
            </div>
        );
    }

    const forwardLabel = isBull ? '多' : '空';
    const reverseLabel = isBull ? '空' : '多';

    return (
        <div className="sd-split-container"
             title={visualForwardPct !== forwardPct
                 ? `实际置信度为 ${forwardPct}%，为保证两侧可点击，视觉区域最小保留 15%`
                 : undefined}>
            <div className="sd-direction-header">
                AI观点：{isBull ? '看多' : '看空'} · {confidenceUnavailable
                ? '置信度不可用，按 50% / 50% 展示'
                : `置信度 ${safeConf}%`}
            </div>
            <div className="sd-split sd-forward-layer"
                 style={{'--split-pct': `${visualForwardPct}%`} as React.CSSProperties}>
                <button
                    className={`sd-btn sd-forward ${isBull ? 'sd-long' : 'sd-short'} ${'FORWARD' === selected ? ' selected' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect('FORWARD');
                    }}
                    aria-label={`${forwardLabel} AI正向 ${forwardPct}%`}
                >
                    <span className="sd-btn-main">{forwardLabel}</span>
                    <span className="sd-btn-sub">{forwardPct.toFixed(2)}%</span>
                </button>
            </div>
            <div className="sd-split sd-reverse-layer"
                 style={{'--split-pct': `${visualForwardPct}%`} as React.CSSProperties}>
                <button
                    className={`sd-btn sd-reverse ${isBull ? 'sd-short' : 'sd-long'} ${'REVERSE' === selected ? ' selected' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelect('REVERSE');
                    }}
                    aria-label={`${reverseLabel} 反向观点 ${reversePct}%`}
                >
                    <span className="sd-btn-main">{reverseLabel}</span>
                    <span className="sd-btn-sub">{reversePct.toFixed(2)}%</span>
                </button>
            </div>
        </div>
    );
};
